import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import {
  FaEdit,
  FaTrash,
  FaSearch,
  FaTimes,
  FaCheckCircle,
  FaBan,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
} from "react-icons/fa";

export default function DeletedProfileTable() {
  const [profiles, setProfiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState(0);

  // ✅ Confirm-delete modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // ✅ States for Search & Filters
  const [filterText, setFilterText] = useState("");
  const [filters, setFilters] = useState({});

  // ✅ States for Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    () => parseInt(localStorage.getItem("pageSize")) || 10,
  );
  const [count, setCount] = useState(0);

  // ✅ Fetch Profiles
  const fetchProfiles = async () => {
    try {
      const params = {
        search: filterText,
        page: page,
        page_size: pageSize,
        ...filters,
      };

      const res = await axios.get("/api/deleted-profiles/", { params });

      // ✅ Assuming API returns: { results: [...], count: 50 }
      setProfiles(res.data.results || res.data);
      if (res.data.count !== undefined) setCount(res.data.count);
    } catch (err) {
      console.error("Error fetching profiles:", err);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [filterText, page, pageSize]);

  const totalPages = Math.ceil(count / pageSize) || 1;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleDelete = async (id) => {
    setPendingDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await axios.delete(`/api/deleted-profiles/${pendingDeleteId}/`);
      fetchProfiles();
    } catch (err) {
      console.error("Error deleting profile:", err);
    } finally {
      setShowDeleteConfirm(false);
      setPendingDeleteId(null);
    }
  };

  const handleEdit = (id) => {
    const profile = profiles.find((p) => p.id === id);
    setSelectedProfile(profile);
    setDeleteStatus(profile.delete_status);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedProfile) return;

    try {
      await axios.put(`/api/deleted-profiles/${selectedProfile.id}/`, {
        delete_status: deleteStatus,
      });

      if (deleteStatus === 1 && selectedProfile.user) {
        await axios.delete(`/api/delete-user/${selectedProfile.user}/`);
      }

      setShowModal(false);
      fetchProfiles();
    } catch (err) {
      console.error("Error updating profile:", err);
    }
  };

  // 🔹 Status badge renderer
  const StatusBadge = ({ value }) => {
    if (value === 1) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
          <FaBan className="text-red-600" /> Deleted
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <FaCheckCircle className="text-green-600" /> Active
      </span>
    );
  };

  // 🔹 Windowed pagination numbers
  const getPageNumbers = () => {
    const pages = [];
    const delta = 1;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    pages.push(1);
    if (left > 2) pages.push("ellipsis-left");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("ellipsis-right");
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  return (
    <div className="min-h-screen bg-slate-50 lg:ml-96 p-3 sm:p-5 lg:p-8 transition-[margin] duration-200">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">
              Deleted Profiles
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Review and manage profiles flagged for deletion.
            </p>
          </div>
          <div className="text-sm text-slate-500">
            <span className="font-semibold text-slate-700">{count}</span> total
            records
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                placeholder="Search by name or phone..."
                className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                value={filterText}
                onChange={(e) => {
                  setPage(1);
                  setFilterText(e.target.value);
                }}
              />
            </div>

            <div className="flex items-center gap-2 text-sm text-slate-600">
              <label htmlFor="pageSize" className="whitespace-nowrap">
                Per page
              </label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                  localStorage.setItem("pageSize", e.target.value);
                }}
                className="border border-slate-300 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        {/* Desktop Table (lg and up) */}
        <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4">Phone</th>
                  <th className="py-3 px-4">Profile ID</th>
                  <th className="py-3 px-4">Gender</th>
                  <th className="py-3 px-4">Reason</th>
                  <th className="py-3 px-4">Deleted At</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {profiles.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-400">
                      No deleted profiles found.
                    </td>
                  </tr>
                )}
                {profiles.map((p) => (
                  <tr
                    key={p.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="py-3 px-4 text-slate-600">{p.email}</td>
                    <td className="py-3 px-4 font-medium text-slate-700">
                      {p.first_name} {p.last_name}
                    </td>
                    <td className="py-3 px-4 text-slate-600">{p.phone}</td>
                    <td className="py-3 px-4 text-slate-500">{p.profile_id}</td>
                    <td className="py-3 px-4 text-slate-600">{p.gender}</td>
                    <td
                      className="py-3 px-4 text-slate-600 max-w-[200px] truncate"
                      title={p.reason}
                    >
                      {p.reason}
                    </td>
                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                      {new Date(p.deleted_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge value={p.delete_status} />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEdit(p.id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-cyan-600 hover:bg-cyan-50 transition-colors"
                          title="Edit status"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                          title="Delete record"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile / Tablet Cards (below lg) */}
        <div className="lg:hidden space-y-3">
          {profiles.length === 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 py-10 text-center text-slate-400">
              No deleted profiles found.
            </div>
          )}
          {profiles.map((p) => (
            <div
              key={p.id}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate">
                    {p.first_name} {p.last_name}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{p.email}</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(p.id)}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-cyan-600 hover:bg-cyan-50 transition-colors"
                    title="Edit status"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                    title="Delete record"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                <div>
                  <p className="text-xs text-slate-400">Phone</p>
                  <p className="text-slate-700 font-medium">{p.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Profile ID</p>
                  <p className="text-slate-700 font-medium">{p.profile_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Gender</p>
                  <p className="text-slate-600">{p.gender}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Deleted At</p>
                  <p className="text-slate-600">
                    {new Date(p.deleted_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-400">Reason</p>
                  <p className="text-slate-600">{p.reason}</p>
                </div>
              </div>

              <div className="mt-3">
                <StatusBadge value={p.delete_status} />
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-slate-500 text-center sm:text-left">
            Showing page{" "}
            <span className="font-semibold text-slate-700">{page}</span> of{" "}
            <span className="font-semibold text-slate-700">{totalPages}</span>
          </p>
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronLeft className="text-xs text-slate-600" />
            </button>

            {getPageNumbers().map((p, i) =>
              p === "ellipsis-left" || p === "ellipsis-right" ? (
                <span key={`${p}-${i}`} className="px-2 text-slate-400 text-sm">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                    page === p
                      ? "bg-cyan-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {p}
                </button>
              ),
            )}

            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronRight className="text-xs text-slate-600" />
            </button>
          </div>
        </div>
      </div>

      {/* ✏️ Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-bold text-slate-800">
                Update Delete Status
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <FaTimes />
              </button>
            </div>

            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-slate-600">
                Delete Status
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteStatus(0)}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                    deleteStatus === 0
                      ? "border-green-500 bg-green-50 text-green-700"
                      : "border-slate-200 text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  <FaCheckCircle />
                  Not Deleted
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteStatus(1)}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                    deleteStatus === 1
                      ? "border-red-500 bg-red-50 text-red-700"
                      : "border-slate-200 text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  <FaBan />
                  Deleted
                </button>
              </div>
              {deleteStatus === 1 && selectedProfile?.user && (
                <p className="mt-3 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Marking as deleted will also permanently remove the associated
                  user account.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🗑️ Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <FaExclamationTriangle className="text-red-500 text-xl" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">
              Delete this record?
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPendingDeleteId(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
